import { Component, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { ModalName } from "src/app/enums/modal-name";
import { PaymentType } from "src/app/enums/payment-type";
import { ISubscriptionPlan } from "src/app/models/client/subscription-plan";
import { ClientService } from "src/app/services/client.service";
import { ModalService } from "src/app/services/modal.service";

@Component({
  templateUrl: "./subscription.component.html",
  styleUrls: ["./subscription.component.scss"],
})
export class SubscriptionComponent implements OnInit {
  subscriptions: ISubscriptionPlan[] = [];
  isSubscriptionsLoading = true;

  currentSubscriptionName: string | null = null;
  activeSubscriptionId = 0;

  isSubscriptionExpired = false;

  isNoSubscription = true;
  changingFromSubjectText = "";
  changingToSubjectText = "";
  errorMessage = "";
  readonly unpaidExtraPagesModal = ModalName.UnpaidExtraPages;

  readonly skeletonRows = Array.from({ length: 3 });

  private readonly _pageNumber = "1";
  private readonly _pageSize = "10";
  private _hasProfileInfo = true;

  constructor(
    private readonly _clientService: ClientService,
    private readonly _modalService: ModalService,
    private readonly _router: Router,
  ) {}

  get currentSubscriptionId() {
    return this.subscriptions.find(subscription => subscription.name === this.currentSubscriptionName)?.id ?? 0;
  }

  get visibleSubscriptions() {
    const isTrialActive = !this.isNoSubscription && !this.isSubscriptionExpired;

    return this.subscriptions.filter(subscription => {
      const isTrial = subscription.name.toLowerCase().includes("trial");

      return !isTrial || (isTrialActive && subscription.name === this.currentSubscriptionName);
    });
  }

  ngOnInit() {
    this.getSubscriptions();
    this.getClientFlags();
  }

  private getSubscriptions() {
    this._clientService
      .getSubscriptions({ pageNumber: this._pageNumber, pageSize: this._pageSize })
      .subscribe(subscriptions => {
        this.subscriptions = subscriptions.records;
        this.isSubscriptionsLoading = false;
      });
  }

  private getClientFlags() {
    this._clientService.getClientFlags().subscribe(flags => {
      this._hasProfileInfo = flags.hasProfileInfo;

      if (flags.hasSubscription) {
        this.getCurrentSubscription();
      }
    });
  }

  private getCurrentSubscription() {
    this._clientService.getCurrentSubscription().subscribe(subscription => {
      this.isNoSubscription = false;
      this.isSubscriptionExpired = new Date(subscription.endedAt).getTime() < Date.now();
      this.currentSubscriptionName = subscription.name;
    });
  }

  selectSubscription(id: number) {
    if (id === this.currentSubscriptionId) {
      return;
    }

    this.activeSubscriptionId = id;
  }

  openChangePopup() {
    this.setChangePlanTxt();
    this._modalService.openModal(ModalName.ChangeSubscription);
  }

  selectNewSubscriptionPlan() {
    this.errorMessage = "";
    if (!this._hasProfileInfo) {
      this._router.navigate(["/settings", "profile"], { queryParams: { profileRequired: "true" } });
      return;
    }

    this._clientService
      .createPaymentLink({ subscriptionId: this.activeSubscriptionId.toString(), type: PaymentType.Subscription })
      .subscribe({
        next: paymentLink => window.open(paymentLink.url, "_blank", "noreferrer"),
        error: error => {
          if (error?.error?.code === "ClientErrors.HasUnpaidExtraPages") {
            this._modalService.openModal(ModalName.UnpaidExtraPages);
            return;
          }
          this.errorMessage = "Failed to start subscription payment. Please try again.";
          this._modalService.openModal(ModalName.Error);
        },
      });
  }

  payExtraPages() {
    this.errorMessage = "";
    this._clientService.createExtraPagesPaymentLink().subscribe({
      next: paymentLink => window.open(paymentLink.url, "_blank", "noreferrer"),
      error: () => {
        this.errorMessage = "Failed to start extra pages payment. Please try again.";
        this._modalService.openModal(ModalName.Error);
      },
    });
  }

  private setChangePlanTxt() {
    const currentPlan = this.subscriptions.find(x => x.id === this.currentSubscriptionId);
    const activePlan = this.subscriptions.find(x => x.id === this.activeSubscriptionId);

    this.changingFromSubjectText = currentPlan?.name ?? "No Subscription";
    this.changingToSubjectText = activePlan?.name ?? "";
  }
}
